import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import Modal from './CarrerasModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Carreras";
// Nota: Estos endpoints y modelos deben estar registrados en sus archivos correspondientes
const endpointList = ENDPOINTS.carrerasAdmin;
const endpointCrud = ENDPOINTS.carreras;
const endpointById = (id) => ENDPOINTS.carrerasById(id);
const modelo = Models.carrera;

export default function CarrerasCatalog() {
    const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('CarrerasLMS');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [instructorMap, setInstructorMap] = useState({});
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        if (!endpointList) return;
        setLoading(true);
        try {
            const res = await axios.get(endpointList);
            setRows((res.data || []).map((x) => modelo ? modelo(x) : x));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // Cargar instructores para el mapeo de nombres
        axios.get(ENDPOINTS.instructores)
            .then(resInstructores => {
                axios.get(ENDPOINTS.empleados)
                    .then(resEmpleados => {
                        axios.get(ENDPOINTS.personas)
                            .then(resPersonas => {
                                const map = {};
                                const emps = resEmpleados.data || [];
                                const pers = resPersonas.data || [];
                                (resInstructores.data || []).forEach(inst => {
                                    const emp = emps.find(e => e.idEmpleado === inst.idEmpleado);
                                    if (emp) {
                                        const per = pers.find(p => p.idPersona === emp.idPersona);
                                        if (per) {
                                            map[inst.idInstructor] = `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim();
                                        }
                                    }
                                });
                                setInstructorMap(map);
                            })
                            .catch(err => console.error("Error al cargar personas para instructores:", err));
                    })
                    .catch(err => console.error("Error al cargar empleados para instructores:", err));
            })
            .catch(err => console.error("Error al cargar instructores:", err));
    }, []);

    const filtered = useMemo(() => {
        const sessionEmpresaId = getSessionEmpresaId();
        const q = query.trim().toLowerCase();
        let res = rows;

        if (sessionEmpresaId) {
            res = res.filter(r => r.idEmpresa === sessionEmpresaId);
        }

        if (!q) return res;

        return res.filter((r) => {
            const nombre = (r.nombre || '').toLowerCase();
            const desc = (r.descripcion || '').toLowerCase();
            const instName = (r.instructorNombreCompleto || instructorMap[r.idInstructor] || '').toLowerCase();

            return nombre.includes(q) || desc.includes(q) || instName.includes(q);
        });
    }, [rows, query, instructorMap]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setModalMode('new');
        setSelectedId(null);
        setEditData(null);
        setModalOpen(true);
    };

    const handleEdit = (carrera) => {
        setSelectedId(carrera.idCarrera);
        setEditData(carrera);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        setDeleteOpen(false);
        setLoading(true);
        try {
            await axios.delete(endpointById(deleteTarget.id));
            triggerAlert('Carrera eliminada con éxito', 'success', 'Éxito');
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            const data = {
                IdCarrera: mode === 'new' ? 0 : selectedId,
                Nombre: payload.nombre,
                Descripcion: payload.descripcion,
                IdInstructor: payload.idInstructor,
                IdCertificado: null,
                Activado: true,
                UrlImagen: payload.urlImagen,
                Publicado: payload.publicado,
                IdEmpresa: mode === 'new' ? getSessionEmpresaId() : (editData?.idEmpresa || getSessionEmpresaId())
            };
            if (mode === 'new') {
                await axios.post(endpointCrud, data);
            } else {
                await axios.put(endpointCrud, data);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >{label}</button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por nombre o descripción…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    {/* Creación gestionada desde la página de Administrar Carreras — no se expone aquí
                    {puedeCrear && (
                        <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                    )}
                    */}
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Descripción</th>
                                    <th>Instructor</th>
                                    <th style={{ textAlign: 'center' }}>Publicado</th>
                                    <th>Empresa</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                                    {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idCarrera}>
                                        <td><strong>{normalizeValue(r.nombre)}</strong></td>
                                        <td>{normalizeValue(r.descripcion)}</td>
                                        <td>{r.instructorNombreCompleto || instructorMap[r.idInstructor] || 'No asignado'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {r.publicado ? 'Sí' : 'No'}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8' }}>
                                                {r.empresaNombre || 'Global'}
                                            </span>
                                        </td>
                                        {puedeEditar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--edit"
                                                    onClick={() => handleEdit(r)}
                                                    aria-label="Editar"
                                                    title="Editar"
                                                >
                                                    <img src={sigmaEdit} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )}
                                        {puedeEliminar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--del"
                                                    onClick={() => askDelete(r.idCarrera, r.nombre)}
                                                    aria-label="Borrar"
                                                    title="Borrar"
                                                >
                                                    <img src={sigmaDelete} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={5 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)} style={{ textAlign: 'center', padding: '1rem' }}>Sin resultados encontrados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                            triggerAlert={triggerAlert}
                            onlyEditInstructor={true}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
